<template>
    <div class="min-h-screen bg-theme-canvas px-5 py-10 md:px-10">
        <div class="mx-auto max-w-3xl">
            <div class="flex items-center justify-between gap-3">
                <img
                    src="/img/logos/complete_logo.png"
                    alt="Tu Catálogo"
                    class="h-9 w-auto"
                />
                <router-link
                    to="/app"
                    class="flex flex-shrink-0 items-center gap-1.5 rounded-lg border border-theme-border bg-theme-card px-3 py-1.5 text-sm font-medium text-theme-base no-underline transition hover:border-theme-primary"
                >
                    ← Volver a la app
                </router-link>
            </div>

            <div
                class="mt-8 rounded-xl bg-theme-primary px-6 py-8 text-theme-inverse"
            >
                <h1 class="text-3xl font-semibold">Ayuda</h1>
                <p class="mt-2 opacity-90">
                    Resolvé tus dudas sobre cómo funciona tu catálogo.
                </p>
            </div>

            <custom-input
                v-model="search"
                label="Buscar un tema"
                class="mt-6"
            />

            <div
                v-if="!isLoading && categories.length > 1"
                class="mt-4 flex flex-wrap gap-2"
            >
                <button
                    type="button"
                    class="rounded-full border px-3 py-1 text-sm font-medium transition"
                    :class="
                        activeCategory === 'all'
                            ? 'border-theme-primary bg-theme-primary text-theme-inverse'
                            : 'border-theme-border text-theme-muted hover:border-theme-primary'
                    "
                    @click="activeCategory = 'all'"
                >
                    Todos
                </button>
                <button
                    v-for="category in categories"
                    :key="category.name"
                    type="button"
                    class="rounded-full border px-3 py-1 text-sm font-medium transition"
                    :class="
                        activeCategory === category.name
                            ? 'border-theme-primary bg-theme-primary text-theme-inverse'
                            : 'border-theme-border text-theme-muted hover:border-theme-primary'
                    "
                    @click="activeCategory = category.name"
                >
                    {{ category.name }}
                </button>
            </div>

            <div
                v-if="isLoading"
                class="mt-10 text-center text-sm text-theme-muted"
            >
                Cargando...
            </div>

            <div
                v-else-if="!filteredCategories.length"
                class="mt-10 text-center text-sm text-theme-muted"
            >
                No encontramos temas que coincidan con "{{ search }}".
            </div>

            <div v-else class="mt-8 space-y-8">
                <div
                    v-for="category in filteredCategories"
                    :key="category.name"
                >
                    <h2
                        class="text-sm font-semibold uppercase tracking-widest text-theme-muted"
                    >
                        {{ category.name }}
                    </h2>
                    <div class="mt-3 grid gap-3 sm:grid-cols-2">
                        <router-link
                            v-for="topic in category.topics"
                            :key="topic.slug"
                            :to="`/wiki/${topic.slug}`"
                            class="rounded-lg border border-theme-border bg-theme-card p-4 no-underline shadow-sm transition hover:-translate-y-0.5 hover:border-theme-primary hover:shadow-md"
                        >
                            <p class="font-semibold text-theme-base">
                                {{ topic.title }}
                            </p>
                            <p class="mt-1 text-sm text-theme-muted">
                                {{ topic.summary }}
                            </p>
                        </router-link>
                    </div>
                </div>
            </div>

            <div
                class="mt-10 flex items-center justify-between gap-3 rounded-lg border border-theme-border bg-theme-card p-4"
            >
                <p class="text-sm text-theme-muted">
                    ¿No encontraste lo que buscabas?
                </p>
                <button
                    type="button"
                    class="flex flex-shrink-0 items-center gap-1.5 rounded-lg bg-theme-success px-3 py-1.5 text-sm font-medium text-white transition hover:opacity-90"
                    @click="promptSupport"
                >
                    <social-icon network="whatsapp" :height="4" :width="4" />
                    Escribinos por soporte
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { SUPPORT_WHATSAPP_LINK } from "@/common/constants";
import { WIKI_TOPIC_SLUGS } from "@/common/fields/wiki-topics";
import { parseWikiFrontmatter } from "@/common/lib/wiki-markdown";

import SocialIcon from "@/components/icons/SocialIcon.vue";
import CustomInput from "@/components/utils/CustomInput.vue";

export default {
    name: "WikiIndex",
    components: { CustomInput, SocialIcon },
    data() {
        return {
            isLoading: true,
            topics: [],
            search: "",
            activeCategory: "all"
        };
    },
    computed: {
        categories() {
            const order = [];
            const byCategory = {};
            this.topics.forEach((topic) => {
                if (!byCategory[topic.category]) {
                    byCategory[topic.category] = [];
                    order.push(topic.category);
                }
                byCategory[topic.category].push(topic);
            });
            return order.map((name) => ({ name, topics: byCategory[name] }));
        },
        filteredCategories() {
            const query = this.search.trim().toLowerCase();
            const byCategory = this.categories.filter(
                (category) =>
                    this.activeCategory === "all" ||
                    category.name === this.activeCategory
            );
            if (!query) return byCategory;
            return byCategory
                .map((category) => ({
                    name: category.name,
                    topics: category.topics.filter((topic) =>
                        topic.searchText.includes(query)
                    )
                }))
                .filter((category) => category.topics.length);
        }
    },
    async mounted() {
        const results = await Promise.all(
            WIKI_TOPIC_SLUGS.map(async (slug) => {
                try {
                    const response = await fetch(`/wiki-content/${slug}.md`);
                    if (!response.ok) return null;
                    const raw = await response.text();
                    const { body, meta } = parseWikiFrontmatter(raw);
                    return { slug, meta, body };
                } catch {
                    return null;
                }
            })
        );

        this.topics = results
            .filter(Boolean)
            .filter((item) =>
                (item.meta.modes || []).includes(this.$operationMode)
            )
            .map((item) => ({
                slug: item.slug,
                title: item.meta.title || item.slug,
                category: item.meta.category || "",
                summary: item.meta.summary || "",
                searchText:
                    `${item.meta.title} ${item.meta.summary} ${item.body}`.toLowerCase()
            }));
        this.isLoading = false;
    },
    methods: {
        promptSupport() {
            this.$toast.show({
                title: "¿Necesitas ayuda?",
                text: "Escríbenos por WhatsApp y te ayudamos.",
                icon: "💬",
                persistent: true,
                action: {
                    label: "Abrir WhatsApp",
                    handler: () => window.open(SUPPORT_WHATSAPP_LINK, "_blank")
                }
            });
        }
    }
};
</script>
